import type { TokenAddress, VaultAddress, WalletAddress } from "@bracket/types";
import * as ABIS from "./abi";

export const SCHEMA = {
	BracketOracle: {
		getRate: {
			args: ["0x00"] as [TokenAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketOracle"]["getRate"]["args"]) => ({ address: ABIS.BracketOracle.address, functionName: "getRate", args: [args[0]], abi: ABIS.BracketOracle.abi }),
			ttl: 60 * 1000,
		},
	},
	BrktETH: {
		getBracketRate: {
			args: [],
			returns: 0n,
			props: () => ({ address: ABIS.BrktETH.address, functionName: "getBracketRate", args: [], abi: ABIS.BrktETH.abi }),
			ttl: 60 * 1000,
		},
		getCollateralInfo: {
			args: ["0x00"] as [TokenAddress],
			returns: {
				collateral: "0x00",
				totalDeposit: 0n,
				whitelisted: false,
			},
			props: (...args: (typeof SCHEMA)["BrktETH"]["getCollateralInfo"]["args"]) => ({ address: ABIS.BrktETH.address, functionName: "getCollateralInfo", args: [args[0]], abi: ABIS.BrktETH.abi }),
			ttl: 60 * 1000,
		},
	},
	BracketVault: {
		accruedBrktTvlFees: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["accruedBrktTvlFees"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "accruedBrktTvlFees", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		accruedManagerPerformanceFees: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["accruedManagerPerformanceFees"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "accruedManagerPerformanceFees", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		accruedManagerTvlFees: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["accruedManagerTvlFees"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "accruedManagerTvlFees", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		assetsPerShare: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["assetsPerShare"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "assetsPerShare", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		balanceOf: {
			args: ["0x00", "0x00"] as [WalletAddress, VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["balanceOf"]["args"]) => ({ address: args[1] as VaultAddress, functionName: "balanceOf", args: [args[0]], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		epoch: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["epoch"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "epoch", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 3600 * 1000,
		},
		isVaultLocked: {
			args: ["0x00"] as [VaultAddress],
			returns: false,
			props: (...args: (typeof SCHEMA)["BracketVault"]["isVaultLocked"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "isVaultLocked", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		sharesOf: {
			args: ["0x00", "0x00"] as [WalletAddress, VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["sharesOf"]["args"]) => ({ address: args[1] as VaultAddress, functionName: "sharesOf", args: [args[0]], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		totalActiveShares: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["totalActiveShares"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalActiveShares", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
			baked: 0n,
		},
		totalDeposits: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaul"]["totalDeposits"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalDeposits", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		totalQueuedDeposits: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaul"]["totalDeposits"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalDeposits", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		totalShares: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["totalShares"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalShares", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		totalSupply: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["totalSupply"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalSupply", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
		totalWithdrawals: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["totalWithdrawals"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalWithdrawals", args: [args[1]], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
			baked: 0n,
		},
		vanityNav: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVault"]["vanityNav"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "vanityNav", args: [], abi: ABIS.BracketVault.abi }),
			ttl: 60 * 1000,
		},
	},
	BracketVaultV2: {
		accruedBrktTvlFees: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["accruedBrktTvlFees"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "accruedBrktTvlFees", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		accruedManagerPerformanceFees: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["accruedManagerPerformanceFees"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "accruedManagerPerformanceFees", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		accruedManagerTvlFees: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["accruedManagerTvlFees"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "accruedManagerTvlFees", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		assetsPerShare: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["assetsPerShare"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "assetsPerShare", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		balanceOf: {
			args: ["0x00", "0x00"] as [WalletAddress, VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["balanceOf"]["args"]) => ({ address: args[1] as VaultAddress, functionName: "balanceOf", args: [args[0]], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		epoch: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["epoch"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "epoch", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 3600 * 1000,
		},
		inactiveSupply: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["inactiveSupply"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "inactiveSupply", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		isVaultLocked: {
			args: ["0x00"] as [VaultAddress],
			returns: false,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["isVaultLocked"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "isVaultLocked", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		sharesOf: {
			args: ["0x00", "0x00"] as [WalletAddress, VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["sharesOf"]["args"]) => ({ address: args[1] as VaultAddress, functionName: "sharesOf", args: [args[0]], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		totalDeposits: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["totalDeposits"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalDeposits", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		totalQueuedDeposits: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["totalQueuedDeposits"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalQueuedDeposits", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		totalShares: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["totalShares"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalShares", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		totalSupply: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["totalSupply"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalSupply", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		totalWithdrawals: {
			args: ["0x00", 0] as [VaultAddress, number], // the vault address and the vault epoch
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["totalWithdrawals"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "totalWithdrawals", args: [args[1]], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
		vanityNav: {
			args: ["0x00"] as [VaultAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["BracketVaultV2"]["vanityNav"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "vanityNav", args: [], abi: ABIS.BracketVaultV2.abi }),
			ttl: 60 * 1000,
		},
	},
	ERC20: {
		balanceOf: {
			args: ["0x00", "0x00"] as [WalletAddress, TokenAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["ERC20"]["balanceOf"]["args"]) => ({ address: args[0] as VaultAddress, functionName: "balanceOf", args: [args[1]], abi: ABIS.ERC20.abi }),
			ttl: 30 * 1000,
		},
	},
	Pricing: {
		getLatestPrice: {
			args: ["0x00"] as [TokenAddress],
			returns: 0n,
			props: (...args: (typeof SCHEMA)["Pricing"]["getLatestPrice"]["args"]) => {
				return { address: ABIS.Pricing.address, functionName: "getLatestPrice", args: [args[0]], abi: ABIS.Pricing.abi };
			},
			ttl: 60 * 1000,
		},
	},
};
